"use client";

import { mergeRefs } from "@react-aria/utils";
import { type AnimationEvent, type ReactNode, type Ref, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import useMeasuredHeight from "../../../hooks/useMeasuredHeight";
import { cn } from "../../../utils";
import { BASE_PANE_KEY, type DialogPaneDirection, useDialogStackContext } from "./context";
import { type DialogPaneMotion, dialogPaneVariants } from "./styles";

const enterMotion = (direction: DialogPaneDirection): DialogPaneMotion =>
  direction === "backward" ? "in-from-left" : "in-from-right";

const exitMotion = (direction: DialogPaneDirection): DialogPaneMotion =>
  direction === "backward" ? "out-to-right" : "out-to-left";

export interface StackPaneProps {
  paneKey: string;
  /** `false` while the pane is being closed — it stays mounted until its exit animation ends. */
  present: boolean;
  /** Called when the pane is dismissed from the outside (Escape, click-outside, `back()`). */
  requestClose?: () => void;
  /** Tailwind width classes the modal box adopts while this pane is the active one. */
  width?: string;
  className?: string;
  children?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

const StackPane = ({
  ref: forwardedRef,
  paneKey,
  present,
  requestClose,
  width,
  className,
  children,
}: StackPaneProps) => {
  const stack = useDialogStackContext();
  const nodeRef = useRef<HTMLDivElement>(null);
  const [measureRef, height] = useMeasuredHeight<HTMLDivElement>();
  const [motion, setMotion] = useState<DialogPaneMotion>("none");

  const isBase = paneKey === BASE_PANE_KEY;
  const isActive = Boolean(stack) && present && stack?.activeKey === paneKey;

  const registerPane = stack?.registerPane;
  const setPanePresent = stack?.setPanePresent;
  const setPaneHeight = stack?.setPaneHeight;
  const setPaneWidth = stack?.setPaneWidth;
  const direction = stack?.direction ?? "initial";

  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;

  useEffect(() => registerPane?.(paneKey, () => closeRef.current?.()), [registerPane, paneKey]);

  useEffect(() => setPanePresent?.(paneKey, present), [setPanePresent, paneKey, present]);

  useEffect(() => setPaneHeight?.(paneKey, height), [setPaneHeight, paneKey, height]);

  useEffect(() => setPaneWidth?.(paneKey, width), [setPaneWidth, paneKey, width]);

  const [prevActive, setPrevActive] = useState(isActive);

  if (prevActive !== isActive) {
    setPrevActive(isActive);
    const exitDirection = present ? direction : "backward";
    setMotion(isActive ? enterMotion(direction) : exitMotion(exitDirection));
  }

  const focusedForRef = useRef(isActive);

  useEffect(() => {
    if (focusedForRef.current === isActive) {
      return;
    }
    focusedForRef.current = isActive;

    const node = nodeRef.current;
    if (isActive && node && !node.contains(document.activeElement)) {
      node.focus({ preventScroll: true });
    }
  }, [isActive]);

  const ref = useMemo(() => mergeRefs(forwardedRef, nodeRef, measureRef), [forwardedRef, measureRef]);

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setMotion("none");
    }
  };

  const pane = (
    <div
      ref={ref}
      tabIndex={-1}
      data-dialog-pane=""
      data-state={isActive ? "active" : "inactive"}
      inert={!isActive}
      className={cn(dialogPaneVariants({ active: isActive, motion }), className)}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );

  if (isBase) {
    return pane;
  }

  return stack?.viewport ? createPortal(pane, stack.viewport) : null;
};

StackPane.displayName = "DialogStackPane";

export default StackPane;
